import React, {FC, useState, useRef, useEffect, useCallback} from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Tooltip
} from '@mui/material';
import {
    CombinedEquipmentRequestEntity,
    EquipmentRequestEntity,
    EquipmentRequestStatusEnum,
    GetEquipmentRequestsInput,
    useExportEquipmentRequestsMutation,
    useGetEquipmentRequestsLazyQuery
} from 'graphql/graphQlApiHooks';
import {useSnackbar} from 'notistack';
import {downloadFile} from 'helpers/downloadFile';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {LoadingButton} from "@mui/lab";
import {SiMicrosoftexcel} from "react-icons/si";
import {useReactiveVar} from "@apollo/client";
import {sportObjectsVars} from "store/reactiveVarsStores/sport-objects/sportObjects";
import {
    EquipmentRequestsFilters,
    equipmentRequestsFiltersVar
} from "store/reactiveVarsStores/equipment-request/filtersVar";
import {
    equipmentRequestListSortOrderVar
} from "store/reactiveVarsStores/equipment-request/equipmentRequestListSortOrder";
import {buildEquipmentRequestOrderBy} from "store/reactiveVarsStores/equipment-request/buildEquipmentRequestOrderBy";
import {darken, lighten, useTheme} from "@mui/material/styles";

/**
 * Кнопка экспорта заявок на оборудование.
 */
export const EquipmentRequestsExport: FC = () => {
    const theme = useTheme();
    const {selectedSportObjectIds} = useReactiveVar(sportObjectsVars);
    const filters = useReactiveVar(equipmentRequestsFiltersVar);
    const sortOrderModel = useReactiveVar(equipmentRequestListSortOrderVar);

    const [open, setOpen] = useState(false);
    const options = ['Экспорт отфильтрованных заявок', 'Экспорт всех заявок'];
    const [selectedIndex, setSelectedIndex] = useState(1);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const anchorRef = useRef<HTMLDivElement>(null);

    const [getEquipmentRequests] = useGetEquipmentRequestsLazyQuery();
    const [exportRequests, {loading}] = useExportEquipmentRequestsMutation();
    const {enqueueSnackbar} = useSnackbar();

    const getFilters = (equipmentRequestsFilters: EquipmentRequestsFilters) => {
        const {applicationDate, requestStatus, name, description, ...restFilters} = equipmentRequestsFilters;
        const result: any = {
            name: name || null,
            description: description || null,
            ...restFilters,
            ...(applicationDate && {
                dateFrom: applicationDate?.startDate?.toISOString(),
                dateTo: applicationDate?.endDate?.toISOString(),
            }),
            ...(requestStatus && requestStatus.length > 0 && {
                requestStatus: requestStatus.map(key => EquipmentRequestStatusEnum[key])
            })
        };
        
        if (result.equipmentTypeIds && result.equipmentTypeIds.length === 0) {
            result.equipmentTypeIds = null;
        }
        if (result.sportKindIds && result.sportKindIds.length === 0) {
            result.sportKindIds = null;
        }
        if (result.sportZoneIds && result.sportZoneIds.length === 0) {
            result.sportZoneIds = null;
        }
        if (result.sportSubZoneIds && result.sportSubZoneIds.length === 0) {
            result.sportSubZoneIds = null;
        }
        
        return result;
    };

    const loadSelectedRequests = useCallback(async (): Promise<string[]> => {
        if (!selectedSportObjectIds?.length) {
            setSelectedIds([]);
            return [];
        }

        const orderBy = buildEquipmentRequestOrderBy(sortOrderModel);
        
        const variables: GetEquipmentRequestsInput = selectedIndex === 0
            ? {
                ...getFilters(filters),
                sportObjectIds: selectedSportObjectIds,
                ...(orderBy && { order: orderBy }),
            } as GetEquipmentRequestsInput
            : {
                name: null,
                description: null,
                requestStatus: null,
                equipmentTypeIds: null,
                sportSubZoneIds: null,
                sportZoneIds: null,
                sportKindIds: null,
                sportObjectIds: selectedSportObjectIds,
                ...(orderBy && { order: orderBy }),
            } as GetEquipmentRequestsInput;

        const res = await getEquipmentRequests({variables: {input: variables}, fetchPolicy: 'cache-and-network'});
        
        const responseCount = res.data?.getEquipmentRequests?.count || 0;
        const singleRequests = res.data?.getEquipmentRequests?.equipmentRequests as EquipmentRequestEntity[] || [];
        const combinedRequests = res.data?.getEquipmentRequests?.combinedEquipmentRequests as CombinedEquipmentRequestEntity[] || [];
        
        const singleRequestIds = singleRequests
            .filter(request => !request.parentCombinedRequestId)
            .map(request => request.id);
        
        const combinedRequestChildIds = (combinedRequests || [])
            .flatMap(combined => combined.childRequests || [])
            .map(child => child.id);
        
        const allIdsSet = new Set([...singleRequestIds, ...combinedRequestChildIds]);
        const allIds = Array.from(allIdsSet);
        
        if (allIds.length !== responseCount) {
            console.warn(`Несоответствие количества: count=${responseCount}, получено ID=${allIds.length}, одиночных=${singleRequestIds.length}, дочерних из объединенных=${combinedRequestChildIds.length}, singleRequests=${singleRequests.length}, combinedRequests=${combinedRequests.length}`);
        }
        
        setSelectedIds(allIds);
        return allIds;
    }, [selectedIndex, filters, selectedSportObjectIds, sortOrderModel, getEquipmentRequests]);

    useEffect(() => {
        loadSelectedRequests();
    }, [loadSelectedRequests]);

    const convertOrderToNested = (orderBy: Record<string, any>): Record<string, any> => {
        const result: Record<string, any> = {};
        for (const [key, value] of Object.entries(orderBy)) {
            const parts = key.split('.');
            let current = result;
            for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) {
                    current[parts[i]] = {};
                }
                current = current[parts[i]];
            }
            current[parts[parts.length - 1]] = value;
        }
        return result;
    };

    const handleClick = async () => {
        const ids = await loadSelectedRequests();
        if (!ids.length) {
            enqueueSnackbar('Нет заявок для экспорта', {variant: 'warning'});
            return;
        }
        const orderBy = buildEquipmentRequestOrderBy(sortOrderModel);
        const nestedOrder = orderBy ? convertOrderToNested(orderBy) : undefined;
        exportRequests({
            variables: {
                ids,
                ...(nestedOrder && { order: nestedOrder })
            }
        })
            .then((res) => downloadFile(res.data.exportedFile))
            .catch((err) => enqueueSnackbar(`Ошибка экспорта файла: ${err?.message}`, {variant: 'error'}));
    };

    const handleMenuItemClick = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        index: number,
    ) => {
        setSelectedIndex(index);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setOpen(false);
    };

    return (
        <Box>
            <ButtonGroup color="inherit" variant="contained" ref={anchorRef} aria-label="split button">
                <Tooltip
                    title={selectedIndex === 0 ?
                        selectedIds?.length
                            ? `Сгенерировать Excel файл из ${selectedIds?.length} заявок`
                            : 'Не найдены заявки для экспорта, попробуйте сбросить фильтры'
                        : 'Сгенерировать Excel файл из всех заявок'}
                >
                    <LoadingButton
                        onClick={handleClick}
                        startIcon={<SiMicrosoftexcel />}
                        disabled={loading || (!selectedIds?.length && selectedIndex === 0)}
                        loading={loading}
                        variant='outlined'
                        size='small'
                        sx={{
                            bgcolor: theme.palette.grey["200"],
                            borderColor: darken(theme.palette.grey["200"], 0.1),
                            '&:hover': {
                                bgcolor: lighten(theme.palette.grey["200"], 0.1),
                            }
                        }}
                    >{options[selectedIndex]}</LoadingButton>
                </Tooltip>
                <Button
                    size="small"
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    variant='outlined'
                    disabled={loading}
                    sx={{
                        padding: '0',
                        bgcolor: theme.palette.grey["200"],
                        borderColor: darken(theme.palette.grey["200"], 0.1),
                        '&:hover': {
                            bgcolor: lighten(theme.palette.grey["200"], 0.1),
                        }
                    }}
                    onClick={handleToggle}
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Popper
                sx={{zIndex: 1}}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {options.map((option, index) => (
                                        <MenuItem
                                            key={option}
                                            selected={index === selectedIndex}
                                            onClick={(event) => handleMenuItemClick(event, index)}
                                        >
                                            {option}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
};
